// src/services/api.ts
import { Gathering, GatheringStatus, Space, Area, GatheringCheckIn, User } from '../types';
import { GatheringCreateRequest } from "../schemas/gatherings";

import { refreshAuthToken } from './firebase';

// Use environment variable for API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Define pagination response interface
interface PaginatedResponse<T> {
  items: T[];
  total_count: number;
  total_pages: number;
  current_page: number;
}

// Cache implementation
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

const cache: Record<string, CacheItem<any>> = {};
const CACHE_TTL = 30 * 1000; // 30 seconds in milliseconds

/**
 * Generates a cache key based on the endpoint and parameters
 * @param endpoint The API endpoint
 * @param params Object containing the parameters
 * @returns A string key for the cache
 */
const generateCacheKey = (endpoint: string, params?: Record<string, any>): string => {
  if (!params) return endpoint;
  
  // Sort the params to ensure consistent keys regardless of object property order
  const sortedParams = Object.keys(params)
    .sort()
    .reduce((acc, key) => {
      if (params[key] !== undefined && params[key] !== null) {
        acc[key] = params[key];
      }
      return acc;
    }, {} as Record<string, any>);
  
  return `${endpoint}:${JSON.stringify(sortedParams)}`;
};

/**
 * Checks if a cache item is valid (not expired)
 * @param cacheItem The cache item to check
 * @returns Boolean indicating if the cache is still valid
 */
const isCacheValid = <T>(cacheItem: CacheItem<T> | undefined): boolean => {
  if (!cacheItem) return false;
  
  const now = Date.now();
  return now - cacheItem.timestamp < CACHE_TTL;
};

/**
 * Invalidates cache entries that match a given prefix
 * @param prefix The prefix to match against cache keys
 */
export const invalidateCache = (prefix?: string): void => {
  if (!prefix) {
    // Clear entire cache if no prefix is provided
    Object.keys(cache).forEach(key => {
      delete cache[key];
    });
    return;
  }
  
  // Clear only cache entries that start with the prefix
  Object.keys(cache).forEach(key => {
    if (key.startsWith(prefix)) {
      delete cache[key];
    }
  });
};

/**
 * Fetches gatherings from the Fabrik API without any filtering
 * @param token Firebase authentication token
 * @returns Promise containing all gatherings
 */
export const fetchAllGatherings = async (token: string): Promise<Gathering[]> => {
  try {
    // No search parameters - use the API defaults
    const url = `${API_BASE_URL}/gatherings/search`;
    const cacheKey = generateCacheKey(url);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data: data.items, timestamp: Date.now() };
    
    return data.items;
  } catch (error) {
    console.error('Error fetching gatherings:', error);
    throw error;
  }
};

/**
 * Fetches all available spaces from the Fabrik API
 * @param token Firebase authentication token
 * @returns Promise containing an array of spaces
 */
export const fetchSpaces = async (token: string): Promise<Space[]> => {
  try {
    const url = `${API_BASE_URL}/spaces/search`;
    const cacheKey = generateCacheKey(url);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data: data.items, timestamp: Date.now() };
    
    return data.items;
  } catch (error) {
    console.error('Error fetching spaces:', error);
    throw error;
  }
};

/**
 * Fetches a specific space by ID
 * @param token Firebase authentication token
 * @param spaceId The ID of the space to fetch
 * @returns Promise containing the space details
 */
export const fetchSpaceById = async (token: string, spaceId: string): Promise<Space> => {
  try {
    const url = `${API_BASE_URL}/spaces/${spaceId}`;
    const cacheKey = generateCacheKey(url);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data;
  } catch (error) {
    console.error(`Error fetching space ${spaceId}:`, error);
    throw error;
  }
};

/**
 * Fetches a specific gathering by ID
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to fetch
 * @returns Promise containing the gathering details
 */
export const fetchGatheringById = async (token: string, gatheringId: string): Promise<Gathering> => {
  try {
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    const cacheKey = generateCacheKey(url);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data;
  } catch (error) {
    console.error(`Error fetching gathering ${gatheringId}:`, error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific status
 * @param token Firebase authentication token
 * @param status Gathering status to filter by
 * @param page Page number (default: 1)
 * @param orderBy Field to order by (default: 'date')
 * @param orderDirection Direction to order (default: 'asc')
 * @param fromDate Optional from_date filter (default: null)
 * @param toDate Optional to_date filter (default: null)
 * @param spaceId Optional space_id filter (default: null)
 * @returns Promise containing paginated gatherings
 */
export const fetchGatheringsByStatus = async (
  token: string,
  status: GatheringStatus,
  page: number = 1,
  orderBy: string = 'date',
  orderDirection: string = 'asc',
  fromDate: string | null = null,
  toDate: string | null = null,
  spaceId: string | null = null
): Promise<PaginatedResponse<Gathering>> => {
  try {
    const searchParams = new URLSearchParams();
    
    // Add pagination parameters - only page, no limit
    searchParams.append('page', page.toString());
    
    // Add status parameter
    searchParams.append('status', status);
    
    // Add sorting parameters
    searchParams.append('order_by', orderBy);
    searchParams.append('order_direction', orderDirection);
    
    // Add date filters if provided
    if (fromDate) {
      searchParams.append('from_date', fromDate);
    }
    
    if (toDate) {
      searchParams.append('to_date', toDate);
    }
    
    // Add space filter if provided
    if (spaceId) {
      searchParams.append('space_id', spaceId);
    }
    
    const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, searchParams);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    // Use the fetchWithTokenRefresh utility
    const data = await fetchWithTokenRefresh(url, token);
    
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data;
  } catch (error) {
    console.error(`Error fetching ${status} gatherings:`, error);
    throw error;
  }
};

/**
 * Fetches a specific area by ID
 * @param token Firebase authentication token
 * @param areaId The ID of the area to fetch
 * @returns Promise containing the area details
 */
export const fetchAreaById = async (token: string, areaId: string): Promise<Area> => {
  try {
    const url = `${API_BASE_URL}/areas/${areaId}`;
    const cacheKey = generateCacheKey(url);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data;
  } catch (error) {
    console.error(`Error fetching area ${areaId}:`, error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific month range (for calendar view)
 * This function fetches gatherings across all statuses for a given month
 * @param token Firebase authentication token
 * @param year Year to fetch (e.g., 2025)
 * @param month Month to fetch (1-12)
 * @param spaceId Optional space_id filter
 * @returns Promise containing gatherings for the month
 */
export const fetchGatheringsForMonth = async (
  token: string,
  year: number,
  month: number,
  spaceId: string | null = null
): Promise<Gathering[]> => {
  try {
    // Calculate first and last day of the month
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0); // Day 0 is the last day of previous month
    
    // Format dates as YYYY-MM-DD
    const fromDate = formatDateForApi(firstDay);
    const toDate = formatDateForApi(lastDay);
    
    // Create array to hold all gatherings
    let allGatherings: Gathering[] = [];
    
    // Fetch gatherings for each status with the date range
    const statusValues = Object.values(GatheringStatus);
    
    // Use Promise.all to fetch all statuses in parallel
    const results = await Promise.all(
      statusValues.map(status => 
        fetchGatheringsByStatus(
          token,
          status,
          1, // Start with page 1
          'date', // Order by date
          'asc', // Ascending order
          fromDate,
          toDate,
          spaceId
        )
      )
    );
    
    // Combine all results
    results.forEach(result => {
      allGatherings = [...allGatherings, ...result.items];
    });
    
    return allGatherings;
  } catch (error) {
    console.error('Error fetching gatherings for month:', error);
    throw error;
  }
};

/**
 * Fetches gatherings for a specific week range (for week view)
 * This function fetches gatherings across all statuses for a given week
 * @param token Firebase authentication token
 * @param startDate The first day of the week
 * @param endDate The last day of the week
 * @param spaceId Optional space_id filter
 * @returns Promise containing gatherings for the week
 */
export const fetchGatheringsForWeek = async (
  token: string,
  startDate: Date,
  endDate: Date,
  spaceId: string | null = null
): Promise<Gathering[]> => {
  try {
    // Format dates as YYYY-MM-DD
    const fromDate = formatDateForApi(startDate);
    const toDate = formatDateForApi(endDate);
    
    // Create array to hold all gatherings
    let allGatherings: Gathering[] = [];
    
    // Fetch gatherings for each status with the date range
    const statusValues = Object.values(GatheringStatus);
    
    // Use Promise.all to fetch all statuses in parallel
    const results = await Promise.all(
      statusValues.map(status => 
        fetchGatheringsByStatus(
          token,
          status,
          1, // Start with page 1
          'date', // Order by date
          'asc', // Ascending order
          fromDate,
          toDate,
          spaceId
        )
      )
    );
    
    // Combine all results
    results.forEach(result => {
      allGatherings = [...allGatherings, ...result.items];
    });
    
    return allGatherings;
  } catch (error) {
    console.error('Error fetching gatherings for week:', error);
    throw error;
  }
};

/**
 * Helper function to format a Date object as YYYY-MM-DD for API
 */
export const formatDateForApi = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Updates the status of a gathering
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param newStatus The new status to set
 * @returns Promise that resolves when the update is complete
 */
export const updateGatheringStatus = async (
  token: string,
  gatheringId: string, 
  newStatus: GatheringStatus
): Promise<void> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // Invalidate cache for this gathering and any status-based queries
    invalidateCache(`/gatherings/${gatheringId}`);
    invalidateCache('/gatherings/search');
    
    // Get the enum key (e.g., "IN_REVIEW") from the value ("In Review")
    const statusKey = Object.keys(GatheringStatus).find(
      key => GatheringStatus[key as keyof typeof GatheringStatus] === newStatus
    );
    
    if (!statusKey) {
      throw new Error(`Invalid gathering status: ${newStatus}`);
    }
    
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/status?gathering_status=${statusKey}`;
    
    await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    // Success - no need to return anything
  } catch (error: unknown) {
    console.error('Error updating gathering status:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update gathering status: ${error.message}`);
    } else {
      throw new Error(`Failed to update gathering status: ${String(error)}`);
    }
  }
};

/**
 * Updates a gathering with new data
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param gatheringData The data to update the gathering with
 * @returns Promise containing the updated gathering
 */
export const updateGatheringDetails = async (
  token: string,
  gatheringId: string,
  gatheringData: Partial<Gathering>
): Promise<Gathering> => {
  try {
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    
    // Check if any date/time fields are being updated
    const isUpdatingDateOrTime = 
      gatheringData.date !== undefined || 
      gatheringData.start_time !== undefined || 
      gatheringData.end_time !== undefined;
    
    // If updating any date/time field, ensure all three are included
    if (isUpdatingDateOrTime) {
      // Fetch the current gathering to get any missing date/time fields
      const currentGathering = await fetchGatheringById(token, gatheringId);
      
      // Ensure all three fields are included in the update
      if (gatheringData.date === undefined) {
        gatheringData.date = currentGathering.date;
      }
      if (gatheringData.start_time === undefined) {
        gatheringData.start_time = currentGathering.start_time;
      }
      if (gatheringData.end_time === undefined) {
        gatheringData.end_time = currentGathering.end_time;
      }
    }
    
    // Prepare the data for the API
    const dataToSend: any = {
      ...gatheringData,
      is_compat_mode: true // Required by the API
    };
    
    // Special handling for area_ids if they exist
    if (gatheringData.areas) {
      dataToSend.area_ids = gatheringData.areas.map(area => area.id);
      delete dataToSend.areas;
    }
    
    // Handle date formatting if needed
    if (dataToSend.date && dataToSend.date instanceof Date) {
      dataToSend.date = formatDateForApi(dataToSend.date);
    }
    
    const response = await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataToSend)
    });
    
    // Invalidate cache for the specific gathering
    invalidateCache(`/gatherings/${gatheringId}`);
    
    return response;
  } catch (error) {
    console.error('Error updating gathering:', error);
    throw error;
  }
};

/**
 * Updates a gathering's schedule (date, time, and areas)
 * @param token Firebase authentication token
 * @param gatheringId The ID of the gathering to update
 * @param scheduleData The new schedule data
 * @returns Promise containing the updated gathering
 */
export const updateGatheringSchedule = async (
  token: string,
  gatheringId: string,
  scheduleData: {
    date: string;
    start_time: string;
    end_time: string;
    space_id: string;
    area_ids: string[];
    duration?: number;
  }
): Promise<Gathering> => {
  try {
    // Convert the schedule data to the format expected by the API
    const dataToSend = {
      date: scheduleData.date,
      start_time: scheduleData.start_time,
      end_time: scheduleData.end_time,
      space_id: scheduleData.space_id,
      area_ids: scheduleData.area_ids,
      duration: scheduleData.duration
    };
    
    const url = `${API_BASE_URL}/gatherings/${gatheringId}`;
    
    // Invalidate cache for this gathering and any related queries
    invalidateCache(`/gatherings/${gatheringId}`);
    invalidateCache('/gatherings/search');
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataToSend)
    });
    
    if (!response.ok) {
      // Add more detailed error logging
      console.error('Error response status:', response.status);
      console.error('Error response status text:', response.statusText);
      
      // Try to get the response body for more details
      try {
        const errorData = await response.text();
        console.error('Error response body:', errorData);
      } catch (e) {
        console.error('Could not read error response body');
      }
      
      if (isTokenExpired(response)) {
        // Try refreshing the token and retry the request
        const newToken = await refreshAuthToken();
        if (newToken) {
          return updateGatheringSchedule(newToken, gatheringId, scheduleData);
        }
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error updating gathering schedule for ${gatheringId}:`, error);
    throw error;
  }
};

/**
 * Fetches gatherings by date range without filtering by status
 * This is the appropriate function to use for calendar views
 * @param token Firebase authentication token
 * @param fromDate Start date in YYYY-MM-DD format
 * @param toDate End date in YYYY-MM-DD format
 * @param page Page number (default: 1)
 * @param orderBy Field to order by (default: 'date')
 * @param orderDirection Order direction (default: 'asc')
 * @param spaceId Optional space_id filter
 * @returns Promise containing paginated gatherings
 */
export const fetchGatheringsByDateRange = async (
  token: string,
  fromDate: string,
  toDate: string,
  page: number = 1,
  orderBy: string = 'date',
  orderDirection: string = 'asc',
  spaceId: string | null = null
): Promise<PaginatedResponse<Gathering>> => {
  try {
    const searchParams = new URLSearchParams();
    
    // Add pagination parameters
    searchParams.append('page', page.toString());
    
    // Add sorting parameters
    searchParams.append('order_by', orderBy);
    searchParams.append('order_direction', orderDirection);
    
    // Add date filters
    searchParams.append('from_date', fromDate);
    searchParams.append('to_date', toDate);
    
    // Add space filter if provided
    if (spaceId) {
      searchParams.append('space_id', spaceId);
    }
    
    const url = `${API_BASE_URL}/gatherings/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, searchParams);
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data;
  } catch (error) {
    console.error(`Error fetching gatherings for date range:`, error);
    throw error;
  }
};

/**
 * Checks if a response indicates an expired token (401 Unauthorized)
 * @param response The fetch response object
 * @returns True if the token is expired, false otherwise
 */
export const isTokenExpired = (response: Response): boolean => {
  return response.status === 401;
};

// Simple token refresh lock to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let lastRefreshTime = 0;
const REFRESH_COOLDOWN = 10000; // 10 seconds between refresh attempts

/**
 * Utility function to fetch data with automatic token refresh if needed
 * @param url The URL to fetch
 * @param token The authentication token
 * @param options Fetch options
 * @param retryCount Number of retry attempts (default: 3)
 * @param retryDelay Delay between retries in ms (default: 1000)
 * @returns Promise resolving to the JSON response
 */
export const fetchWithTokenRefresh = async (
  url: string,
  token: string,
  options: RequestInit = {},
  retryCount: number = 3,
  retryDelay: number = 1000
): Promise<any> => {
  
  // Create request options with the token
  const requestOptions: RequestInit = {
    ...options,
    headers: {
      ...(options.headers as Record<string, string>),
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
  
  // Try the request with retries
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt <= retryCount; attempt++) {
    try {
      // If this is a retry attempt, wait before trying again
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
      }
      
      // Make the request
      let response = await fetch(url, requestOptions);
      
      // If the response is successful, return the parsed JSON
      if (response.ok) {
        return response.json();
      }
      
      try {
        const errorText = await response.text();
      } catch (e) {
      }
      
      // If the token is expired, try to refresh it
      if (isTokenExpired(response)) {
        try {
          // Check if we're already refreshing or if we've refreshed recently
          const now = Date.now();
          if (!isRefreshing && (now - lastRefreshTime) > REFRESH_COOLDOWN) {
            // Set the lock to prevent other requests from refreshing
            isRefreshing = true;
            lastRefreshTime = now;
            
            try {
              // Try to refresh the token
              const newToken = await refreshAuthToken();
              
              if (newToken) {
                // Update the authorization header with the new token
                const newRequestOptions: RequestInit = {
                  ...requestOptions,
                  headers: {
                    ...(requestOptions.headers as Record<string, string>),
                    'Authorization': `Bearer ${newToken}`
                  }
                };
                
                // Retry the request with the new token
                const retryResponse = await fetch(url, newRequestOptions);
                
                if (retryResponse.ok) {
                  return retryResponse.json();
                }
                
                // If the retry failed, throw a more specific error
                throw new Error(`API retry failed: ${retryResponse.status}`);
              }
            } finally {
              // Release the lock when done
              isRefreshing = false;
            }
          } else {
            // If we're already refreshing or refreshed recently, wait a bit and try again with the same token
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Retry the request with the same token (which might have been refreshed by another request)
            const retryResponse = await fetch(url, requestOptions);
            
            if (retryResponse.ok) {
              return retryResponse.json();
            }
          }
        } catch (refreshError) {
          // Silent failure - no need to log errors during token refresh
          throw new Error('Token refresh failed');
        }
      }
      
      // For server errors (5xx), continue with retry logic
      if (response.status >= 500) {
        lastError = new Error(`Server error: ${response.status}`);
        continue;
      }
      
      // For other errors, throw with error body
      const errorText = await response.text();
      console.error('API error response:', response.status, errorText);
      throw new Error(`API error: ${response.status} - ${errorText}`);
    } catch (error) {
      lastError = error as Error;
      
      // If this is not a server error or we've reached max retries, throw
      if (!(error instanceof Error && error.message.includes('Server error')) || attempt === retryCount) {
        throw error;
      }
    }
  }
  
  // If we've exhausted all retries, throw the last error
  throw lastError;
};

/**
 * Fetches all areas for a specific space
 * @param token Firebase authentication token
 * @param spaceId The ID of the space to fetch areas for
 * @returns Promise containing an array of areas
 */
export const fetchAreasBySpaceId = async (token: string, spaceId: string): Promise<Area[]> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('space_id', spaceId);
    
    const url = `${API_BASE_URL}/areas/search?${searchParams.toString()}`;
    const cacheKey = generateCacheKey(url, { space_id: spaceId });
    
    if (cache[cacheKey] && isCacheValid(cache[cacheKey])) {
      return cache[cacheKey].data.items;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (isTokenExpired(response)) {
        // Try refreshing the token and retry the request
        const newToken = await refreshAuthToken();
        if (newToken) {
          return fetchAreasBySpaceId(newToken, spaceId);
        }
      }
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    cache[cacheKey] = { data, timestamp: Date.now() };
    
    return data.items;
  } catch (error) {
    console.error(`Error fetching areas for space ${spaceId}:`, error);
    throw error;
  }
};

/**
 * Updates a gathering's status as an admin, with optional notes
 * @param token Authentication token
 * @param gatheringId ID of the gathering to update
 * @param newStatus New status to set
 * @param notes Optional notes to include (required for NEED_MORE_INFO status)
 * @returns Promise that resolves when the update is complete
 */
export const updateGatheringStatusAsAdmin = async (
  token: string,
  gatheringId: string, 
  newStatus: GatheringStatus,
  notes?: string
): Promise<void> => {
  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    // Invalidate cache for this gathering and any status-based queries
    invalidateCache(`/gatherings/${gatheringId}`);
    invalidateCache('/gatherings/search');
    
    // Get the enum key (e.g., "NEED_MORE_INFO") from the value ("Needs More Info")
    const statusKey = Object.keys(GatheringStatus).find(
      key => GatheringStatus[key as keyof typeof GatheringStatus] === newStatus
    );
    
    if (!statusKey) {
      throw new Error(`Invalid gathering status: ${newStatus}`);
    }
    
    // Build the URL for the admin endpoint
    const url = `${API_BASE_URL}/gatherings/${gatheringId}/admin/status`;

    const requestBody: { status: string; notes?: string } = {
      status: newStatus // Send the status value (e.g., "Need More Info")
    };
    
    // Only include notes if they're provided and status is NEED_MORE_INFO
    if (notes && statusKey === 'NEED_MORE_INFO') {
      requestBody.notes = notes;
    }
    
    await fetchWithTokenRefresh(url, token, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(requestBody)
    });
    
    // Success - no need to return anything
  } catch (error: unknown) {
    console.error('Error updating gathering status as admin:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to update gathering status: ${error.message}`);
    } else {
      throw new Error(`Failed to update gathering status: ${String(error)}`);
    }
  }
};

export const fetchCheckInsByGatheringId = async (
  token: string,
  gatheringId: string
): Promise<GatheringCheckIn[]> => {
  const url = `${API_BASE_URL}/gatherings/${gatheringId}/checkins`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return await response.json();
}


/**
 * Fetches all users (optionally with filters)
 * @param token Firebase authentication token
 * @returns Promise containing an array of users
 */
export const fetchUsers = async (token: string): Promise<User[]> => {
  const url = `${API_BASE_URL}/users/search?page=1&limit=100&order_by=email&order_direction=asc`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  const data = await response.json();
  return data.items;
};

export const fetchAllUsers = async (token: string): Promise<User[]> => {
  let allUsers: User[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages) {
    const url = `${API_BASE_URL}/users/search?page=${page}&limit=100&order_by=email&order_direction=asc`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    allUsers = [...allUsers, ...data.items];
    totalPages = data.total_pages;
    page++;
  }

  return allUsers;
};

export const createGatheringAsAdmin = async (
  token: string,
  ownerId: string,
  gatheringData: GatheringCreateRequest
): Promise<Gathering> => {
  const url = `${API_BASE_URL}/gatherings/admin?owner_id=${ownerId}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(gatheringData),
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  const data = await response.json();

  // Invalidate caches so list views update
  invalidateCache("/gatherings/search");
  invalidateCache("/gatherings");

  return data;
};